<template>
  <v-container
    fluid
    pa-1
    style="height: calc(100vh - 130px)"
  >
    <v-row dense>
      <v-col cols="12">
        <v-toolbar
          dense
        >
          <v-btn
            :disabled="loading"
            :loading="loading"
            class="mr-2 px-1 white--text"
            color="light-green"
            min-width="40"
            @click="getData"
          >
            <v-icon>mdi-sync</v-icon>
          </v-btn>

          <v-btn-toggle
            v-model="orderKdsStatus"
            dense
            mandatory
          >
            <v-btn
              :active-class="[$t('Kds.Status.Order.Open.Color'), 'white--text'].join(' ')"
              :value="$t('Kds.Status.Order.Open.Code')"
            >
              <v-chip
                :color="$t('Kds.Status.Order.Open.Color') + ' darken-1'"
                class="rounded justify-center count-chip px-1 mr-2"
                dark
                label
                small
              >
                {{ ordersOpenCount }}
              </v-chip>

              {{ $t('Kds.Status.Order.Open.Name') }}
            </v-btn>

            <v-btn
              :active-class="[$t('Kds.Status.Order.Hold.Color'), 'white--text'].join(' ')"
              :value="$t('Kds.Status.Order.Hold.Code')"
            >
              <v-chip
                :color="$t('Kds.Status.Order.Hold.Color') + ' darken-1'"
                class="rounded justify-center count-chip px-1 mr-2"
                dark
                label
                small
              >
                {{ ordersOnHoldCount }}
              </v-chip>

              {{ $t('Kds.Status.Order.Hold.Name') }}
            </v-btn>

            <v-btn
              :active-class="[$t('Kds.Status.Order.Complete.Color'), 'white--text'].join(' ')"
              :value="$t('Kds.Status.Order.Complete.Code')"
            >
              <v-chip
                :color="$t('Kds.Status.Order.Complete.Color') + ' darken-1'"
                class="rounded justify-center count-chip px-1 mr-2"
                dark
                label
                small
              >
                {{ ordersCompletedCount }}
              </v-chip>

              {{ $t('Kds.Status.Order.Complete.Name') }}
            </v-btn>

            <v-btn
              :active-class="[$t('Kds.Status.Order.Cancel.Color'), 'white--text'].join(' ')"
              :value="$t('Kds.Status.Order.Cancel.Code')"
            >
              <v-chip
                :color="$t('Kds.Status.Order.Cancel.Color') + ' darken-1'"
                class="rounded justify-center count-chip px-1 mr-2"
                dark
                label
                small
              >
                {{ ordersCanceledCount }}
              </v-chip>

              {{ $t('Kds.Status.Order.Cancel.Name') }}
            </v-btn>
          </v-btn-toggle>

          <v-spacer />

          <v-btn-toggle
            v-model="orderTypeFilter"
            dense
            mandatory
          >
            <v-btn
              :active-class="[$t('Kds.OrderType.All.Color'), $vuetify.theme.dark ? 'white--text' : ''].join(' ')"
              :value="parseInt($t('Kds.OrderType.All.Code'))"
            >
              {{ $t('Kds.OrderType.All.Name') }}
            </v-btn>

            <v-btn
              :active-class="[$t('Kds.OrderType.Table.Color'), 'white--text'].join(' ')"
              :value="parseInt($t('Kds.OrderType.Table.Code'))"
            >
              <v-chip
                :color="$t('Kds.OrderType.Table.Color')"
                class="rounded justify-center count-chip px-1 mr-2"
                dark
                label
                small
              >
                {{ ordersTableCount }}
              </v-chip>

              {{ $t('Kds.OrderType.Table.Name') }}
            </v-btn>

            <v-btn
              :active-class="[$t('Kds.OrderType.Collection.Color'), 'white--text'].join(' ')"
              :value="parseInt($t('Kds.OrderType.Collection.Code'))"
            >
              <v-chip
                :color="$t('Kds.OrderType.Collection.Color')"
                class="rounded justify-center count-chip px-1 mr-2"
                dark
                label
                small
              >
                {{ ordersCollectionCount }}
              </v-chip>

              {{ $t('Kds.OrderType.Collection.Name') }}
            </v-btn>

            <v-btn
              :active-class="[$t('Kds.OrderType.Delivery.Color'), 'white--text'].join(' ')"
              :value="parseInt($t('Kds.OrderType.Delivery.Code'))"
            >
              <v-chip
                :color="$t('Kds.OrderType.Delivery.Color')"
                class="rounded justify-center count-chip px-1 mr-2"
                dark
                label
                small
              >
                {{ ordersDeliveryCount }}
              </v-chip>

              {{ $t('Kds.OrderType.Delivery.Name') }}
            </v-btn>
          </v-btn-toggle>

          <v-chip
            class="ml-2 justify-center"
            color="info"
            dark
            label
            style="height: 36px; min-width: 80px;"
          >
            <real-time-clock
              class="text-h5 font-weight-bold"
              class-seconds="text-body-1 font-weight-bold"
              display-seconds
            />
          </v-chip>
        </v-toolbar>
      </v-col>
    </v-row>

    <v-row
      v-if="ordersFiltered.length"
      dense
    >
      <v-col cols="12">
        <div class="d-flex fill-height overflow-auto">
          <template v-for="(order, index) in ordersFiltered">
            <v-lazy
              :key="`order-lazy-${order.Id}-${index}`"
              :max-width="358"
              :min-width="358"
              :options="{threshold: 0.25}"
              :width="358"
              transition="fade-transition"
            >
              <div
                :key="`order-${order.Id}-${index}`"
                class="d-flex flex-column pa-1"
              >
                <kds-order-card
                  :fill-height="true"
                  :hide-item-actions="hideKdsItemActions"
                  :hide-order-actions="hideKdsOrderActions"
                  :min-width="350"
                  :order="order"
                  @on-kds-order-click="onKdsOrderShowActionsDialogClick"
                />
              </div>
            </v-lazy>
          </template>
        </div>
      </v-col>
    </v-row>

    <v-row
      v-else
      class="fill-height align-center justify-center"
      dense
    >
      <v-col
        class="text-center"
        cols="12"
      >
        <template v-if="viewData">
          <v-data-table-no-data
            :no-data-obj="$t('Kds.List.NoData')"
            :subtitle="noDataTitle"
          />
        </template>

        <template v-else>
          <v-progress-circular
            color="grey"
            indeterminate
            size="48"
            width="2"
          />
        </template>
      </v-col>
    </v-row>

    <kds-order-actions-dialog
      :order="selectedOrder"
      :visible.sync="kdsOrderActionsDialogVisible"
    />
  </v-container>
</template>

<script>

import KdsOrderCard from '@/components/kds/KdsOrderCard.vue'
import OrderTypeEnum from '@/api/Enums/OrderTypeEnum.js'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum.js'
import Auth from '@/mixins/auth.js'
import AppData from '@/mixins/appdata.js'
import KdsOrderActionsDialog from '@/components/kds/KdsOrderActionsDialog.vue'
import VDataTableNoData from '@/components/common/data-table/VDataTableNoData.vue'
import AutoUpdateApiData from '@/mixins/api/autoUpdateApiData.js'
import KdsOrderStatusEnum from '@/api/Enums/KdsOrderStatusEnum.js'
import * as SocketKdsHelper from '@/api/SocketKdsHelper.js'
import {sortBy} from '@/lib/helper/helper.js'
import RealTimeClock from '@/components/cds/realTimeClock.vue'

export default {
  name: 'Kds',

  components: {RealTimeClock, VDataTableNoData, KdsOrderActionsDialog, KdsOrderCard},

  mixins: [Auth, AppData, AutoUpdateApiData],

  dataStore: {
    kdsOrders: 'kds.Orders'
  },

  data() {
    return {
      loading                     : false,
      viewData                    : null,
      selectedOrder               : null,
      hideKdsOrderActions         : false,
      hideKdsItemActions          : false,
      kdsOrderActionsDialogVisible: false,
      orderKdsStatus              : KdsOrderStatusEnum.OPEN,
      orderTypeFilter             : 0,
      autoUpdateApiDataOptions    : {
        timeout  : this.timeoutInMins(5),
        autoStart: false,
        callback : 'getData'
      }
    }
  },

  computed: {
    OrderTypeEnum() {
      return OrderTypeEnum
    },

    orders() {
      return this.viewData ?? []
    },

    ordersOpen() {
      return this.orders?.filter(o => o.Status === KdsOrderStatusEnum.OPEN || !o.Status) || []
    },

    ordersOnHold() {
      return this.orders?.filter(o => o.Status === KdsOrderStatusEnum.HOLD) || []
    },

    ordersCompleted() {
      return this.orders?.filter(o => o.Status === KdsOrderStatusEnum.COMPLETE) || []
    },

    ordersCanceled() {
      return this.orders?.filter(o => o.Status === KdsOrderStatusEnum.CANCEL) || []
    },

    ordersWithStatus() {
      if (this.orderKdsStatus === KdsOrderStatusEnum.HOLD) return this.ordersOnHold
      if (this.orderKdsStatus === KdsOrderStatusEnum.COMPLETE) return this.ordersCompleted
      if (this.orderKdsStatus === KdsOrderStatusEnum.CANCEL) return this.ordersCanceled

      return this.ordersOpen
    },

    ordersFiltered() {
      const orderTypes = [OrderTypeEnum.DELIVERY, OrderTypeEnum.COLLECTION]
      const orderTypeFilter = parseInt(this.orderTypeFilter)

      return this.ordersWithStatus.filter(o => orderTypes.includes(orderTypeFilter) ? o.SourceTypeId !== SourceTypeEnum.TABLE && o.OrderTypeId === orderTypeFilter : orderTypeFilter === -1 ? o.SourceTypeId === SourceTypeEnum.TABLE : true)
    },

    ordersOpenCount() {
      return this.ordersOpen.length
    },

    ordersOnHoldCount() {
      return this.ordersOnHold.length
    },

    ordersCompletedCount() {
      return this.ordersCompleted.length
    },

    ordersCanceledCount() {
      return this.ordersCanceled.length
    },

    ordersAllCount() {
      return this.ordersWithStatus.length
    },

    ordersTableCount() {
      return this.ordersWithStatus.filter(o => o.SourceTypeId === SourceTypeEnum.TABLE).length
    },

    ordersCollectionCount() {
      return this.ordersWithStatus.filter(o => o.SourceTypeId !== SourceTypeEnum.TABLE && o.OrderTypeId === OrderTypeEnum.COLLECTION).length
    },

    ordersDeliveryCount() {
      return this.ordersWithStatus.filter(o => o.SourceTypeId !== SourceTypeEnum.TABLE && o.OrderTypeId === OrderTypeEnum.DELIVERY).length
    },

    noDataTitle() {
      let orderSource = ''
      if (parseInt(this.orderTypeFilter) === OrderTypeEnum.COLLECTION) orderSource = this.$t('Kds.List.NoData.SourceCollection')
      if (parseInt(this.orderTypeFilter) === OrderTypeEnum.DELIVERY) orderSource = this.$t('Kds.List.NoData.SourceDelivery')
      if (parseInt(this.orderTypeFilter) === -1) orderSource = this.$t('Kds.List.NoData.SourceTable')

      if (this.orderKdsStatus === KdsOrderStatusEnum.OPEN) return this.$t('Kds.List.NoData.TitleOpen', {source: orderSource})
      if (this.orderKdsStatus === KdsOrderStatusEnum.HOLD) return this.$t('Kds.List.NoData.TitleOnHold', {source: orderSource})
      if (this.orderKdsStatus === KdsOrderStatusEnum.COMPLETE) return this.$t('Kds.List.NoData.TitleCompleted', {source: orderSource})
      if (this.orderKdsStatus === KdsOrderStatusEnum.CANCEL) return this.$t('Kds.List.NoData.TitleCanceled', {source: orderSource})

      return this.$t('Kds.List.NoData.Title')
    }
  },

  watch: {},

  created() {
    this.$bus.$on(window.SocketCommand.Kds.All, this.onGetDataResponse)
    this.$bus.$on(window.SocketCommand.Kds.Order.ChangePriority, this.getOrder)
    this.$bus.$on(window.SocketCommand.Kds.Order.ChangeStatus, this.getOrder)
    this.$bus.$on(window.SocketCommand.Kds.Order.ChangeItemStatus, this.getOrder)

    this.getData()
  },

  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Kds.All, this.onGetDataResponse)
    this.$bus.$off(window.SocketCommand.Kds.Order.ChangePriority, this.getOrder)
    this.$bus.$off(window.SocketCommand.Kds.Order.ChangeStatus, this.getOrder)
    this.$bus.$off(window.SocketCommand.Kds.Order.ChangeItemStatus, this.getOrder)
  },

  methods: {
    onKdsOrderShowActionsDialogClick(order) {
      if (!order) return

      this.selectedOrder = order
      this.kdsOrderActionsDialogVisible = true
    },

    getOrder(data, payload) {
      if (!data?.status === 'success' || !payload) return

      SocketKdsHelper.getKdsOrder(payload.OrderUid, 'update')
    },

    getData() {
      if (this.loading) return

      this.loading = true
      window.callAS(window.SocketCommand.Kds.All, {})
    },

    onGetDataResponse(result) {
      this.loading = false

      if (!Array.isArray(result)) return

      this.$DataStore.kds.orders = this.viewData = sortBy(result, 'Priority', 'asc')

      if (this.selectedOrder && result?.length) {
        const order = result.find(order => order.Id === this.selectedOrder.Id)
        this.selectedOrder = order || null
      }
    }
  }
}
</script>

<style scoped>
:deep(.v-toolbar__content) {
  padding: 0 6px 0 6px !important;
}

.count-chip {
  min-width: 30px;
  pointer-events: none;
}
</style>
